import { Injectable } from '@angular/core';
import {
  ActivityDetails,
  CLOSED_LOST_STAGE_CODE,
  CLOSED_WON_STAGE_CODE,
  SalesFolderRestService,
  StageAdvancementResultDto,
  StageTriggerEvaluateRequestDto,
} from '@gh/builder-shared-data';
import { DictionaryService, TextDictionary } from '@gh/common-shared';
import { isMessageBasket } from '@gh/core-messages';
import { ToastActionKind, ToastRef, ToastService, ToastSeverity } from '@gh/core-ui';
import { TranslationService } from '@gh/core-mls';
import { tap$, catchError$, finalize$ } from '@gh/rx/operators';
import { EMPTY$, of$ } from '@gh/rx';
import { Observable, Subject } from 'rxjs';
import { ID } from '@gh/common-shared-data';

import { hasActivityId, shouldEvaluateActivityDone } from './activity-done-trigger.policy';
import {
  shouldEvaluateQuoteAuthorised,
  shouldEvaluateQuoteCreated,
  shouldEvaluateQuotePrinted,
} from './quote-stage-trigger.policy';
import { shouldEvaluateSiteCreated } from './site-stage-trigger.policy';

const SILENT_TRIGGER_EVENTS = new Set(['manual', 'undo']);
const WILL_NOT_COMPLETE_TRIGGER = 'status_changed_to_will_not_complete';
/** Reasons the backend refuses an undo (DefaultStageAdvancementService). Anything else is a generic failure. */
const UNDO_ERROR_KEYS = [
  'msg_stage_undo_not_latest',
  'msg_stage_undo_completed_sale',
  'msg_stage_undo_into_closed_lost',
  'msg_stage_undo_invalid_event',
];
const UNDO_FAILED_KEY = 'msg_stage_undo_failed';
/** Reasons the backend refuses to close a lead as lost (LostLeadFields, NotProceedingPolicy). */
const CLOSED_LOST_REASON_REQUIRED_KEYS = [
  'msg_not_proceeding_reason_required',
  'msg_not_proceeding_reason_other_required',
];
const CLOSED_LOST_MOVE_BLOCKED_KEY = 'msg_closed_lost_move_blocked';
const CLOSED_LOST_NOT_APPLIED_KEY = 'msg_stage_closed_lost_not_applied';

/** The first of `keys` the server sent in `error` (with or without the msg_ prefix). */
function serverMessageKey(error: unknown, keys: string[]): string | undefined {
  if (!isMessageBasket(error)) {
    return void 0;
  }
  return keys.find((key) => error.has(key) || error.has(key.replace(/^msg_/, '')));
}

export function undoErrorMessageKey(error: unknown): string {
  return serverMessageKey(error, UNDO_ERROR_KEYS) || UNDO_FAILED_KEY;
}

/**
 * Warning after a Not Proceeding save whose lead did not reach Closed Lost, or undefined when it did.
 * A lead in the regional Closed Won stays there (the backend blocks the move): saving again does not help,
 * so that case gets its own title. For the rest, the server's reason (if known) is shown under the title.
 */
export function closedLostNotAppliedToast(result?: StageAdvancementResultDto,
                                          error?: unknown): { title: string; message?: string } | undefined {
  if (result && result.advanced) {
    return void 0;
  }
  if (result && `${result.newStage}` === `${CLOSED_LOST_STAGE_CODE}`) {
    return void 0;
  }
  if (result && `${result.newStage}` === `${CLOSED_WON_STAGE_CODE}`
    || serverMessageKey(error, [CLOSED_LOST_MOVE_BLOCKED_KEY])) {
    return { title: CLOSED_LOST_MOVE_BLOCKED_KEY };
  }
  return { title: CLOSED_LOST_NOT_APPLIED_KEY, message: serverMessageKey(error, CLOSED_LOST_REASON_REQUIRED_KEYS) };
}

const STAGE_CODE_TO_NAME: Record<number, string> = {
  0: 'NEW',
  1: 'INTRODUCTION',
  2: 'DISCOVERY',
  3: 'SITE_DISCUSSION',
  4: 'CONCEPT_DISCUSSION',
  5: 'PROPOSAL',
  6: 'CLOSED',
  7: 'CLOSED_LOST',
  8: 'CLOSED_WON',
};

const TRIGGER_REASON_KEYS: Record<string, string> = {
  activity_done: 'lbl_stage_trigger_reason_activity_done',
  quote_created: 'lbl_stage_trigger_reason_quote_created',
  quote_printed: 'lbl_stage_trigger_reason_quote_printed',
  quote_authorised: 'lbl_stage_trigger_reason_quote_authorised',
  site_row_created: 'lbl_stage_trigger_reason_site_row_created',
  sale_completed: 'lbl_stage_trigger_reason_sale_completed',
  status_changed_to_will_not_complete: 'lbl_stage_trigger_reason_status_changed_to_will_not_complete',
};

@Injectable()
export class StageAdvancementNotificationService {
  private stageDictionary?: TextDictionary;
  /** Stage changes (aelEventId) with an undo request on the way: a second click or Ctrl+Z is ignored. */
  private readonly undoInFlight = new Set<number>();
  private readonly stageUndoneSubject = new Subject<StageAdvancementResultDto>();
  private readonly stageAdvancedSubject = new Subject<StageAdvancementResultDto>();
  /** Fires after a stage undo is saved, so open views can reload. */
  readonly stageUndone$ = this.stageUndoneSubject.asObservable();
  /** Fires after an automatic stage change is saved, so open views can reload. */
  readonly stageAdvanced$ = this.stageAdvancedSubject.asObservable();

  constructor(private toastService: ToastService,
              private salesFolderRest: SalesFolderRestService,
              private dictionaryService: DictionaryService,
              private translationService: TranslationService) {
  }

  start(): void {
    this.dictionaryService.get('STAGE').pipe(catchError$(() => EMPTY$))
      .subscribe((dictionary) => this.stageDictionary = dictionary);
  }

  notifyIfAdvanced(result: StageAdvancementResultDto): void {
    if (!result || !result.advanced || SILENT_TRIGGER_EVENTS.has(result.triggerEvent || '')) {
      return;
    }
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }

    const previousStage = this.stageLabel(result.previousStage);
    const newStage = this.stageLabel(result.newStage);
    const clientName = result.clientName || this.translationService.get('lbl_stage_toast_lead_fallback');
    const message = this.translationService.get('msg_stage_auto_advanced_body', {
      clientName,
      previousStage,
      newStage,
      reason: this.triggerReason(result),
    });

    // Each toast closes itself on Undo (button or Ctrl+Z), not whichever toast was shown last.
    const toastRef: ToastRef = this.toastService.show({
      severity: ToastSeverity.Info,
      title: this.translationService.get('msg_stage_auto_advanced_title', { newStage }),
      message,
      // A completed sale created a job; its stage is not undoable (the backend refuses it too).
      action: result.undoAvailable && result.aelEventId != null && result.triggerEvent !== 'sale_completed' ? {
        kind: ToastActionKind.Undo,
        label: this.translationService.get('lbl_toast_undo_status_change'),
        run: () => this.undo(result, toastRef),
      } : undefined,
    });
  }

  /**
   * @param toastRef the toast the undo came from; closed straight away
   */
  undo(result: StageAdvancementResultDto, toastRef?: ToastRef): void {
    const { salesFolderId, aelEventId } = result;
    if (!salesFolderId || aelEventId == null || this.undoInFlight.has(aelEventId)) {
      return;
    }
    this.undoInFlight.add(aelEventId);
    if (toastRef) {
      toastRef.close();
    }
    this.salesFolderRest.undoStageChange(salesFolderId, aelEventId).pipe(
      finalize$(() => this.undoInFlight.delete(aelEventId)),
    ).subscribe({
      next: (undone) => {
        this.toastService.show({
          severity: ToastSeverity.Success,
          title: this.translationService.get('msg_stage_change_undone'),
        });
        this.stageUndoneSubject.next(undone);
      },
      error: (error) => {
        this.toastService.show({
          severity: ToastSeverity.Warning,
          title: this.translationService.get(undoErrorMessageKey(error)),
        });
      },
    });
  }

  notifyAfterActivityMarkedDone(salesFolderId: ID | undefined,
                                activity: Partial<ActivityDetails> | undefined,
                                wasDone: boolean): Observable<StageAdvancementResultDto | undefined> {
    if (!salesFolderId || !activity || !hasActivityId(activity.id)) {
      return of$(undefined);
    }
    if (!shouldEvaluateActivityDone({
      wasDone,
      isDone: !!activity.isDone,
      activityType: activity.activityType,
      milestoneEvent: activity.milestoneEvent,
    })) {
      return of$(undefined);
    }
    return this.evaluateAndNotify(salesFolderId, 'activity_done', {
      sourceType: 'activity',
      activityId: activity.id,
      triggerMetadata: {
        activity_id: activity.id != null ? String(activity.id) : '',
        activity_type: activity.activityType != null ? String(activity.activityType) : '',
        source: 'activity',
      },
    });
  }

  notifyAfterQuoteCreated(salesFolderId: ID | undefined, quotationId: ID | undefined): void {
    if (!shouldEvaluateQuoteCreated({ salesFolderId, quotationId })) {
      return;
    }
    this.notifyQuoteEvent(salesFolderId!, 'quote_created', quotationId!);
  }

  notifyAfterQuotePrinted(salesFolderId: ID | undefined, quotationId: ID | undefined, entryPoint: unknown): void {
    if (!shouldEvaluateQuotePrinted({ salesFolderId, quotationId, entryPoint })) {
      return;
    }
    this.notifyQuoteEvent(salesFolderId!, 'quote_printed', quotationId!);
  }

  notifyAfterQuoteAuthorised(salesFolderId: ID | undefined, quotationId: ID | undefined, isApproving: boolean): void {
    if (!shouldEvaluateQuoteAuthorised({ salesFolderId, quotationId, isApproving })) {
      return;
    }
    this.notifyQuoteEvent(salesFolderId!, 'quote_authorised', quotationId!);
  }

  notifyAfterSiteCreated(salesFolderId: ID | undefined, siteId: ID | undefined): void {
    if (!shouldEvaluateSiteCreated({ salesFolderId, siteId })) {
      return;
    }
    this.evaluateAndNotify(salesFolderId!, 'site_row_created', {
      sourceType: 'site',
      siteId,
      triggerMetadata: {
        site_id: String(siteId),
        source: 'site',
      },
    }).subscribe();
  }

  evaluateAndNotify(salesFolderId: StageAdvancementResultDto['salesFolderId'],
                    eventType: string,
                    extras: Omit<StageTriggerEvaluateRequestDto, 'eventType'> = {}): Observable<StageAdvancementResultDto> {
    return this.salesFolderRest.evaluateStageTrigger(salesFolderId, { eventType, ...extras }).pipe(
      tap$((result) => {
        this.notifyIfAdvanced(result);
        if (result && result.advanced) {
          this.stageAdvancedSubject.next(result);
        } else if (eventType === WILL_NOT_COMPLETE_TRIGGER) {
          this.warnIfClosedLostNotApplied(result);
        }
      }),
      catchError$((error) => {
        // The status is already saved; without the move the lead sits outside Closed Lost. Saving it again retries.
        // Other triggers stay silent: the activity / quote / site save they follow has succeeded.
        if (eventType === WILL_NOT_COMPLETE_TRIGGER) {
          this.warnIfClosedLostNotApplied(void 0, error);
        }
        return EMPTY$;
      }),
    );
  }

  private warnIfClosedLostNotApplied(result?: StageAdvancementResultDto, error?: unknown): void {
    const toast = closedLostNotAppliedToast(result, error);
    if (!toast) {
      return;
    }
    this.toastService.show({
      severity: ToastSeverity.Warning,
      title: this.translationService.get(toast.title),
      message: toast.message ? this.translationService.get(toast.message) : void 0,
    });
  }

  private notifyQuoteEvent(salesFolderId: ID, eventType: string, quotationId: ID): void {
    this.evaluateAndNotify(salesFolderId, eventType, {
      sourceType: 'quote',
      quotationId,
      triggerMetadata: {
        quote_id: String(quotationId),
        source: 'quote',
      },
    }).subscribe();
  }

  private stageLabel(code: number): string {
    if (this.stageDictionary) {
      const byCode = this.stageDictionary.get(String(code));
      if (byCode) {
        return byCode;
      }
    }
    const name = STAGE_CODE_TO_NAME[code];
    if (name === 'DISCOVERY') {
      const qualified = this.translationService.get('dictionary:STAGE:QUALIFIED');
      if (qualified && qualified !== 'dictionary:STAGE:QUALIFIED') {
        return qualified;
      }
    }
    if (name) {
      const translated = this.translationService.get(`dictionary:STAGE:${name}`);
      if (translated && translated !== `dictionary:STAGE:${name}`) {
        return translated;
      }
      return name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    }
    return String(code);
  }

  private triggerReason(result: StageAdvancementResultDto): string {
    const event = (result.triggerEvent || '').toLowerCase();
    const reasonKey = TRIGGER_REASON_KEYS[event] || TRIGGER_REASON_KEYS[result.triggerEvent || ''];
    if (reasonKey) {
      return this.translationService.get(reasonKey);
    }
    const description = (result.triggerEventDescription || '').trim();
    return description ? description.charAt(0).toLowerCase() + description.slice(1) : event;
  }
}
